import { useState } from "react";
import type { ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import { useSnackbarErrorHandler } from "utils/errorHandling";
import { ActionType, useContactMetierActions } from "providers";
import SaveIcon from "@mui/icons-material/Save";
import type { AffecterNouveauPosteByIdsDto, Poste } from "models";
import { usePostes } from "hooks";
import { PosteAutoComplete } from "components/inputs";
import { ToastMessages } from "enums";
import { enqueueSnackbar } from "notistack";
import { contactsMetiersService } from "services";

export function EditMultipleContactsMetiersDialog(): ReactNode {
  const {
    actionType,
    closeActionDialog,
    selectedContactMetiers,
    isActionDialogOpen,
    onMultipleEdit,
  } = useContactMetierActions();
  const { postes, isPostesLoading } = usePostes();
  const { catchErrors, isLoading } = useSnackbarErrorHandler();
  const [nouveauPoste, setNouveauPoste] = useState<Poste | null>(null);

  function confirm(): void {
    async function confirmAndClose(): Promise<void> {
      if (selectedContactMetiers.length === 0 || nouveauPoste == null) return;

      const payload: AffecterNouveauPosteByIdsDto = {
        poste: nouveauPoste,
        contactsIds: selectedContactMetiers.map((cm) => cm.id),
      };

      await contactsMetiersService.multipleModifyPoste(payload);
      closeActionDialog();
      enqueueSnackbar({
        variant: "success",
        autoHideDuration: 2000,
        preventDuplicate: false,
        message: ToastMessages.CONTACT_METIER_EDIT_SUCCESS,
      });
      onMultipleEdit?.(payload.contactsIds, payload.poste);
    }
    void catchErrors(confirmAndClose);
  }

  return (
    <Dialog
      open={isActionDialogOpen && actionType === ActionType.EDIT_MULTIPLE}
      onClose={closeActionDialog}
      fullWidth>
      <DialogTitle>Éditer le poste d'une sélection de contacts métiers</DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={2}>
          <Typography>
            Veuiller sélectionner le nouveau poste pour les {selectedContactMetiers.length} Contacts
            Métiers sélectionnés.
          </Typography>
          <PosteAutoComplete
            postes={postes}
            label="Poste *"
            onChange={setNouveauPoste}
            value={nouveauPoste}
            isLoading={isPostesLoading}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeActionDialog} color="error">
          Annuler
        </Button>
        <Button
          onClick={confirm}
          variant="contained"
          autoFocus
          loading={isLoading}
          loadingPosition="start"
          // TODO utiliser react-hook-form pour forcer cette vérification
          // et afficher un message au user
          disabled={nouveauPoste == null}
          startIcon={<SaveIcon />}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
